/**
 * 공지사항 등록/수정 페이지
 */

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, Form, Button, Spinner, Tabs, Tab, Alert } from 'react-bootstrap';
import AdminLayout from '../../components/layout/AdminLayout';
import RichTextEditor from '../../components/common/RichTextEditor';
import { noticeService } from '../../services/noticeService';
import { Notice } from '../../types/notice';

const NoticeForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const isNew = location.pathname.includes('/new');
  const noticeId = isNew ? 0 : (id ? parseInt(id, 10) : 0);

  const [activeTab, setActiveTab] = useState<'ko' | 'en' | 'th'>('ko');
  const [formData, setFormData] = useState<Partial<Notice>>({
    title: { ko: '', en: '', th: '' },
    content: { ko: '', en: '', th: '' },
    notice_type: 'general',
    priority: 0,
    is_main_visible: false,
    status: 'active',
  });

  const { data: notice, isLoading } = useQuery({
    queryKey: ['notice', noticeId],
    queryFn: () => noticeService.getNotice(noticeId),
    enabled: !!noticeId && noticeId > 0 && !isNew,
  });

  const saveMutation = useMutation({
    mutationFn: (data: Partial<Notice>) =>
      isNew ? noticeService.createNotice(data) : noticeService.updateNotice(noticeId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notices'] });
      alert(isNew ? '공지사항이 등록되었습니다.' : '공지사항이 수정되었습니다.');
      navigate('/admin/notices');
    },
    onError: (error: any) => {
      alert(`저장 실패: ${error.message}`);
    },
  });

  useEffect(() => {
    if (notice) {
      setFormData({
        title: notice.title || { ko: '', en: '', th: '' },
        content: notice.content || { ko: '', en: '', th: '' },
        notice_type: notice.notice_type,
        priority: notice.priority,
        is_main_visible: notice.is_main_visible,
        start_date: notice.start_date,
        end_date: notice.end_date,
        status: notice.status,
      });
    }
  }, [notice]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // 한국어 제목과 내용은 필수
    if (!formData.title?.ko?.trim() || !formData.content?.ko?.trim()) {
      alert('한국어 제목과 내용은 필수입니다.');
      return;
    }

    saveMutation.mutate(formData);
  };

  const handleTitleChange = (lang: 'ko' | 'en' | 'th', value: string) => {
    setFormData((prev) => ({
      ...prev,
      title: {
        ...prev.title,
        [lang]: value,
      },
    }));
  };

  const handleContentChange = (lang: 'ko' | 'en' | 'th', value: string) => {
    setFormData((prev) => ({
      ...prev,
      content: {
        ...prev.content,
        [lang]: value,
      },
    }));
  };

  if (isLoading && !isNew) {
    return (
      <AdminLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spinner animation="border" variant="primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>{isNew ? '공지사항 등록' : '공지사항 수정'}</h1>
          <Button variant="secondary" onClick={() => navigate('/admin/notices')}>
            목록으로
          </Button>
        </div>

        <Form onSubmit={handleSubmit}>
          <Card className="mb-4">
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label style={{ color: '#fff' }}>공지 유형</Form.Label>
                <Form.Select
                  value={formData.notice_type}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      notice_type: e.target.value as 'general' | 'urgent' | 'maintenance',
                    }))
                  }
                  style={{ backgroundColor: '#2E2E3A', color: '#fff', border: '1px solid #3A4B5C' }}
                >
                  <option value="general">일반</option>
                  <option value="urgent">긴급</option>
                  <option value="maintenance">점검</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label style={{ color: '#fff' }}>우선순위</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  max="100"
                  value={formData.priority || 0}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      priority: parseInt(e.target.value, 10) || 0,
                    }))
                  }
                  style={{ backgroundColor: '#2E2E3A', color: '#fff', border: '1px solid #3A4B5C' }}
                />
                <Form.Text style={{ color: '#888' }}>0-100 사이의 값 (높을수록 우선순위 높음)</Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="메인 화면 노출"
                  checked={formData.is_main_visible || false}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      is_main_visible: e.target.checked,
                    }))
                  }
                  style={{ color: '#fff' }}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label style={{ color: '#fff' }}>시작일</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={formData.start_date ? formData.start_date.substring(0, 16) : ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      start_date: e.target.value ? `${e.target.value}:00` : undefined,
                    }))
                  }
                  style={{ backgroundColor: '#2E2E3A', color: '#fff', border: '1px solid #3A4B5C' }}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label style={{ color: '#fff' }}>종료일</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={formData.end_date ? formData.end_date.substring(0, 16) : ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      end_date: e.target.value ? `${e.target.value}:00` : undefined,
                    }))
                  }
                  style={{ backgroundColor: '#2E2E3A', color: '#fff', border: '1px solid #3A4B5C' }}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label style={{ color: '#fff' }}>상태</Form.Label>
                <Form.Select
                  value={formData.status}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      status: e.target.value as 'active' | 'inactive',
                    }))
                  }
                  style={{ backgroundColor: '#2E2E3A', color: '#fff', border: '1px solid #3A4B5C' }}
                >
                  <option value="active">활성</option>
                  <option value="inactive">비활성</option>
                </Form.Select>
              </Form.Group>
            </Card.Body>
          </Card>

          <Card className="mb-4">
            <Card.Body>
              <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k as 'ko' | 'en' | 'th')}
                className="mb-3"
              >
                <Tab eventKey="ko" title="한국어 (필수)">
                  <Form.Group className="mb-3">
                    <Form.Label style={{ color: '#fff' }}>제목 (한국어) *</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.title?.ko || ''}
                      onChange={(e) => handleTitleChange('ko', e.target.value)}
                      placeholder="공지사항 제목을 입력하세요..."
                      required
                      style={{ backgroundColor: '#2E2E3A', color: '#fff', border: '1px solid #3A4B5C' }}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={{ color: '#fff' }}>내용 (한국어) *</Form.Label>
                    <RichTextEditor
                      value={formData.content?.ko || ''}
                      onChange={(value) => handleContentChange('ko', value)}
                      placeholder="공지사항 내용을 입력하세요..."
                      height="400px"
                    />
                  </Form.Group>
                </Tab>

                <Tab eventKey="en" title="English">
                  <Form.Group className="mb-3">
                    <Form.Label style={{ color: '#fff' }}>Title (English)</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.title?.en || ''}
                      onChange={(e) => handleTitleChange('en', e.target.value)}
                      placeholder="Enter notice title..."
                      style={{ backgroundColor: '#2E2E3A', color: '#fff', border: '1px solid #3A4B5C' }}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={{ color: '#fff' }}>Content (English)</Form.Label>
                    <RichTextEditor
                      value={formData.content?.en || ''}
                      onChange={(value) => handleContentChange('en', value)}
                      placeholder="Enter notice content..."
                      height="400px"
                    />
                  </Form.Group>
                </Tab>

                <Tab eventKey="th" title="ไทย">
                  <Form.Group className="mb-3">
                    <Form.Label style={{ color: '#fff' }}>หัวข้อ (ไทย)</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.title?.th || ''}
                      onChange={(e) => handleTitleChange('th', e.target.value)}
                      placeholder="กรอกหัวข้อประกาศ..."
                      style={{ backgroundColor: '#2E2E3A', color: '#fff', border: '1px solid #3A4B5C' }}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={{ color: '#fff' }}>เนื้อหา (ไทย)</Form.Label>
                    <RichTextEditor
                      value={formData.content?.th || ''}
                      onChange={(value) => handleContentChange('th', value)}
                      placeholder="กรอกเนื้อหาประกาศ..."
                      height="400px"
                    />
                  </Form.Group>
                </Tab>
              </Tabs>
            </Card.Body>
          </Card>

          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" onClick={() => navigate('/admin/notices')}>
              취소
            </Button>
            <Button variant="primary" type="submit" disabled={saveMutation.isPending}>
              {saveMutation.isPending ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  저장 중...
                </>
              ) : (
                '저장'
              )}
            </Button>
          </div>
        </Form>
      </div>
    </AdminLayout>
  );
};

export default NoticeForm;

